import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCircle2, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import {
  getMyProfile,
  upsertMyProfile,
  uploadAvatar,
  importProviderAvatar,
} from '@/services/profileRepo';
import { composeAvatarUrl } from '@/lib/avatar';

// Soft client-side limits. Not DB-enforced — if abuse shows up we add
// check constraints. Keep them generous; the goal is to prevent obviously
// broken values, not to police prose.
const MAX_DISPLAY_NAME = 80;
const MAX_PEN_NAME = 80;
const MAX_BIO = 500;

// File size cap on the *uploaded* (pre-transcode) image. Larger files
// are accepted; we transcode + downscale to ~512px WebP regardless.
// This guard exists only so a phone HEIC dropped in by mistake doesn't
// stall the main thread for 10 seconds before we throw.
const MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // 15 MB

const initialsFrom = (name, email) => {
  const source = (name || email || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || source[0].toUpperCase();
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const fileInputRef = useRef(null);
  // Guards the provider-avatar backfill so multiple renders / Strict
  // Mode double-invokes don't re-import in parallel.
  const providerImportTriedRef = useRef(false);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Form state — initialised from profile or auth fallback once loaded.
  const [displayName, setDisplayName] = useState('');
  const [penName, setPenName] = useState('');
  const [bio, setBio] = useState('');

  // Gate: must be signed in.
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error('Please sign in to view your profile');
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const row = await getMyProfile();
      setProfile(row);
      // First-time fallback: pre-fill display_name from the auth user's
      // sign-in name (Google full_name etc.) so the field isn't empty.
      setDisplayName(row?.display_name ?? user.name ?? '');
      setPenName(row?.pen_name ?? '');
      setBio(row?.bio ?? '');
    } catch (err) {
      console.error('Profile load error:', err);
      toast.error(err?.message || 'Could not load your profile');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user || authLoading) return;
    void load();
  }, [user, authLoading, load]);

  // First-load provider-avatar backfill.
  //
  // If the profile row has no avatar_object_key yet but the OAuth
  // provider (Google etc.) gave us a `user.avatar_url` at sign-in,
  // copy that image into R2 once so it shows up everywhere — story
  // cards, search results, public attribution — not just for the
  // signed-in user looking at their own profile. After this runs,
  // `avatar_object_key` is the single source of truth and no
  // fallback logic is needed on read.
  //
  // Failure here is non-fatal: the user can still upload manually,
  // and their own view falls back to the provider URL at render
  // time. We log but don't toast — the user didn't ask for this.
  useEffect(() => {
    if (!user || loading) return;
    if (providerImportTriedRef.current) return;
    if (profile?.avatar_object_key) return;
    if (!user.avatar_url) return;

    providerImportTriedRef.current = true;
    setUploadingAvatar(true);
    (async () => {
      try {
        const updated = await importProviderAvatar(user.avatar_url);
        setProfile(updated);
      } catch (err) {
        console.warn('Provider avatar import failed (non-fatal):', err);
      } finally {
        setUploadingAvatar(false);
      }
    })();
  }, [user, loading, profile?.avatar_object_key]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const updated = await upsertMyProfile({
        display_name: displayName,
        pen_name: penName,
        bio,
      });
      setProfile(updated);
      toast.success('Profile saved');
    } catch (err) {
      console.error('Profile save error:', err);
      toast.error(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again re-fires onChange.
    if (e.target) e.target.value = '';
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error('That image is too large. Pick something under 15 MB.');
      return;
    }
    setUploadingAvatar(true);
    try {
      const updated = await uploadAvatar(file);
      setProfile(updated);
      toast.success('Avatar updated');
    } catch (err) {
      console.error('Avatar upload error:', err);
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Cache-bust on every updated_at change so the new avatar replaces
  // the old one even though they share an object key. The provider
  // avatar is shown as a fallback only while the R2 copy is still
  // being backfilled (first profile load) — once `avatar_object_key`
  // is set, R2 takes over for good.
  const avatarUrl = composeAvatarUrl(
    profile?.avatar_object_key,
    profile?.updated_at,
    user?.avatar_url,
  );
  const initials = initialsFrom(displayName, user?.email);

  if (authLoading || (user && loading)) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C05621]" />
          <p className="mt-4 text-[#6B665E]">Loading your profile…</p>
        </div>
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <PageHeader>
        <ToolbarCaptionButton
          label="Home"
          shortLabel="Home"
          onClick={() => navigate('/')}
          data-testid="profile-home-btn"
        />
      </PageHeader>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-start gap-3 mb-10">
          <div className="rounded-xl bg-[#FBF4EB] border border-[#E6E2D6] p-3">
            <UserCircle2 className="h-8 w-8 text-[#C05621]" />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-serif text-[#2D2A26]">Your profile</h2>
            <p className="text-[#6B665E] mt-1 max-w-xl">
              How you appear on Katha Mithra. Your display name, pen name, and bio
              show up next to stories you publish.
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSave}
          className="bg-white rounded-2xl border border-[#E6E2D6] p-6 sm:p-8 space-y-8"
        >
          {/* Avatar block */}
          <section className="flex items-center gap-5">
            <div className="relative">
              <Avatar className="h-20 w-20 border border-[#E6E2D6]">
                {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
                <AvatarFallback className="bg-[#FBF4EB] text-[#C05621] text-xl font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
              {uploadingAvatar ? (
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                </div>
              ) : null}
            </div>
            <div className="flex-1 min-w-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                data-testid="profile-avatar-pick-btn"
              >
                <Camera className="h-4 w-4 mr-2" />
                {profile?.avatar_object_key ? 'Change photo' : 'Add photo'}
              </Button>
              <p className="text-xs text-[#6B665E] mt-2">
                JPG, PNG, or WebP. We resize and convert to WebP so it stays small.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleAvatarPick}
                data-testid="profile-avatar-file-input"
              />
            </div>
          </section>

          {/* Display name */}
          <section className="space-y-2">
            <Label htmlFor="display_name" className="text-[#2D2A26]">
              Display name
            </Label>
            <Input
              id="display_name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={MAX_DISPLAY_NAME}
              placeholder="Your name"
              data-testid="profile-display-name-input"
            />
            <p className="text-xs text-[#6B665E]">
              Shown on your profile. Pulled from your sign-in account by default.
            </p>
          </section>

          {/* Pen name */}
          <section className="space-y-2">
            <Label htmlFor="pen_name" className="text-[#2D2A26]">
              Pen name <span className="text-[#6B665E] font-normal">(optional)</span>
            </Label>
            <Input
              id="pen_name"
              value={penName}
              onChange={(e) => setPenName(e.target.value)}
              maxLength={MAX_PEN_NAME}
              placeholder="The byline you publish under"
              data-testid="profile-pen-name-input"
            />
            <p className="text-xs text-[#6B665E]">
              Optional byline used when you publish stories. Leave blank to use
              your display name.
            </p>
          </section>

          {/* Bio */}
          <section className="space-y-2">
            <Label htmlFor="bio" className="text-[#2D2A26]">
              Bio <span className="text-[#6B665E] font-normal">(optional)</span>
            </Label>
            <Textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={MAX_BIO}
              rows={4}
              placeholder="A short note about yourself."
              data-testid="profile-bio-input"
            />
            <p className="text-xs text-[#6B665E] flex justify-between">
              <span>A few lines about you — shown alongside stories you publish.</span>
              <span>{bio.length}/{MAX_BIO}</span>
            </p>
          </section>

          <div className="flex justify-end pt-2 border-t border-[#E6E2D6]">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#C05621] hover:bg-[#A1441A] text-white"
              data-testid="profile-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
